import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import {
  FieldTypeEnum,
  IClientCurrencySettings,
  IClientSettings,
  ILegalIdentifierSettings,
  IOrganizationalUnit,
  IOrganizationalUnitManagers,
  IViewers,
  addViewersToClientContactsUnit,
  addViewersToTeamsUnit,
  addViewersToUnit,
  autocompleteClientName,
  createOrganizationalUnitManagers,
  deleteOUManagerMember,
  deleteViewersFromClientContactsUnit,
  deleteViewersFromTeamsUnit,
  deleteViewersFromUnit,
  getSelectedOUClients,
  selectedOrganizationalUnitClientManagers,
  selectedOrganizationalUnitManagers,
  selectedOrganizationalUnitTeams,
  CustomFieldEnum,
  ICustomField,
} from 'wisestep-api';
import { activityFormattedDate, getFormatDate } from 'wisestep-base';
import { object, string } from 'yup';

import { IOption } from '../../../components/SettingsIndex';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { TitleAndBreadCrumbs } from '../../invoicing/helper/InvocieSettingsHelper';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../teams/helper/defaultValues';

export const clientSettingsIndexOptions: IOption[] = [
  { label: 'Currency', url: '/currencies' },
  { label: 'File Categories', url: '/app-settings/file_categories' },
  { label: 'Legal Identifiers', url: '/legal_identifiers' },
  { label: 'Fee Arrangements', url: '/position_levels' },
  { label: 'Customize Client Fields', url: '/app-settings/customize-client-fields' },
  { label: 'Customize Contact Fields', url: '/app-settings/customize-contact-fields' },
  { label: 'Customize Lead Fields', url: '/app-settings/customize-lead-fields' },
  { label: 'Customize CRM Job Fields', url: '/app-settings/customize-crm-job-fields' },
];

export const fieldTypes = [
  'TextBox',
  'Number',
  'Date',
  'Location',
  'DateAndTime',
  'URL',
  'Tags',
  'Dropdown',
  'Users',
  'Checkbox',
] as const;

export const FieldNameMap = {
  TextBox: 'TextBox',
  Number: 'Number',
  Date: 'Date',
  Location: 'Location',
  DateAndTime: 'Date And Time',
  URL: 'URL',
  Tags: 'Tags',
  Dropdown: 'Dropdown',
  Users: 'Users',
  Checkbox: 'Checkbox',
};

export const FieldIconMap = {
  TextBox: TextFieldsOutlinedIcon,
  Number: PinOutlinedIcon,
  Date: CalendarMonthOutlinedIcon,
  Location: LocationOnOutlinedIcon,
  DateAndTime: AccessTimeOutlinedIcon,
  URL: LinkOutlinedIcon,
  Tags: LocalOfferOutlinedIcon,
  Dropdown: DnsOutlinedIcon,
  Users: GroupOutlinedIcon,
  Checkbox: CheckBoxOutlinedIcon,
};

export const clientSettingsDefaultPayload: IClientSettings = {
  name: '',
  active: true,
  id: '',
  workspaceId: '',
  createdBy: '',
  createdOn: null,
  modifiedBy: '',
  modifiedOn: null,
  description: '',
};

export const legalIdentifierDefaultPayload: ILegalIdentifierSettings = {
  ...clientSettingsDefaultPayload,
  country: '',
};

export const OrganizationalUnitDefaultPayload: IOrganizationalUnit = {
  ...clientSettingsDefaultPayload,
  scopeType: '',
  selectedTeams: [],
};

export const currencySettingsDefaultQuery: IClientCurrencySettings = {
  ...clientSettingsDefaultPayload,
  symbol: '',
  active: true,
  default: false,
};

export const hardcodedClientFields: ICustomField[] = [
  {
    label: 'Client Name',
    type: FieldTypeEnum.TextBox,
    name: 'name',
    required: true,
  },
  {
    label: 'Linkedin Url',
    type: FieldTypeEnum.TextBox,
    name: 'linkedinUrl',
    required: true,
  },
  {
    label: 'Location',
    type: FieldTypeEnum.Location,
    name: 'location',
  },
  {
    label: 'Client Status',
    type: FieldTypeEnum.TextBox,
    name: 'clientStatusId',
  },
  {
    label: 'Client Owner',
    type: FieldTypeEnum.TextBox,
    name: 'ownerId',
    required: true,
  },
  {
    label: 'Industry Type',
    type: FieldTypeEnum.TextBox,
    name: 'industry',
  },
  {
    label: 'Source',
    type: FieldTypeEnum.TextBox,
    name: 'source',
  },
  {
    label: 'Tags',
    type: FieldTypeEnum.Tags,
    name: 'tags',
  },
  {
    label: 'Description',
    type: FieldTypeEnum.TextBox,
    name: 'description',
  },
].map((field) => ({ ...field, isDefault: true }));

const stringToColor = (string: string) => {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};
export const stringAvatar = (name: string) => {
  return {
    sx: {
      bgcolor: stringToColor(name),
      color: 'common.black',
      width: 30,
      height: 30,
      fontSize: 15,
    },
    children: `${name.split(' ')[0][0]}`,
  };
};

export const getClients = async (clientNameSearch: string) => {
  const result = await autocompleteClientName(`${encodeURIComponent(clientNameSearch)}`);
  const clients = result.data.map((c) => ({ ...c, id: c.value }));
  return clients;
};

export const tabstype = {
  Teams: 'Teams',
  'Account Managers': 'Account Managers',
  Clients: 'Clients',
  'Client Contacts': 'Client Contacts',
};
export const gobackUrl = {
  Teams: '/workspace-users-and-teams/organizational-unit-recruitment',
  'Account Managers': '/workspace-users-and-teams/account-manager-units',
  Clients: '/workspace-users-and-teams/client-units',
  'Client Contacts': '/workspace-users-and-teams/client-contacts-units',
};

export interface IClientOptions {
  id: string;
  name: string;
  status?: string;
}
export interface IClientCheckBoxListOptions {
  [key: string]: IClientOptions;
}

export enum MethodType {
  Get = 'GET',
  Add = 'ADD',
  Delete = 'DELETE',
}

export enum ScopeType {
  Teams = 'Teams',
  Clients = 'Clients',
  Account_Managers = 'Account Managers',
  Client_Contacts = 'Client Contacts',
}

export enum RoleTypes {
  User = 'User',
  Lead = 'Lead',
  Analyst = 'Analyst',
  Head = 'Head',
  ACCOUNT_MANAGER = 'Account Manager',
}

export const viewersApisCalls = async (
  scopeType: string,
  func: string,
  organizationalId?: string,
  payload?: IViewers
) => {
  const apis = {
    [ScopeType.Teams]: {
      [MethodType.Get]: () => selectedOrganizationalUnitTeams(organizationalId ?? ''),
      [MethodType.Add]: () => payload && addViewersToTeamsUnit(payload),
      [MethodType.Delete]: () => payload && deleteViewersFromTeamsUnit(payload),
    },
    [ScopeType.Clients]: {
      [MethodType.Get]: () => getSelectedOUClients(organizationalId ?? ''),
      [MethodType.Add]: () => payload && addViewersToUnit(payload),
      [MethodType.Delete]: () => payload && deleteViewersFromUnit(payload),
    },
    [ScopeType.Client_Contacts]: {
      [MethodType.Get]: () => selectedOrganizationalUnitClientManagers(organizationalId ?? ''),
      [MethodType.Add]: () => payload && addViewersToClientContactsUnit(payload),
      [MethodType.Delete]: () => payload && deleteViewersFromClientContactsUnit(payload),
    },
    [ScopeType.Account_Managers]: {
      [MethodType.Get]: () => selectedOrganizationalUnitManagers(organizationalId ?? ''),
      [MethodType.Add]: () => {
        if (!payload) return;
        const req: IOrganizationalUnitManagers[] = payload.viewersIds.map((item) => ({
          organizationalId: payload.organizationalId,
          accountManagerId: item,
          type: 'Viewers',
        }));
        return createOrganizationalUnitManagers(req);
      },
      [MethodType.Delete]: async () => {
        if (!payload) return;
        const res = await selectedOrganizationalUnitManagers(payload.organizationalId ?? '');
        const data: any[] = res?.data ?? [];
        const id = data.find((item) => item.accountManagerId === payload.viewersIds[0])?.value;
        return deleteOUManagerMember(id ?? '');
      },
    },
  };
  const apiFunc = apis?.[scopeType]?.[func];
  return await apiFunc();
};

export type ClientSettingType = 'FILE_CATAGORIES' | 'LEGAL_IDENTIFIERS';

export const ClientBreadCrumbListAndTitle: Record<ClientSettingType, TitleAndBreadCrumbs> = {
  FILE_CATAGORIES: {
    Title: 'File Catagories',
    BreadcrumbsList: getSettingBreadCrumbList(
      ISettingBreadCrumbTypes.CLIENTS,
      Pages['FILE_CATAGORIES']
    ),
  },
  LEGAL_IDENTIFIERS: {
    Title: 'Legal Identifiers',
    BreadcrumbsList: getSettingBreadCrumbList(
      ISettingBreadCrumbTypes.CLIENTS,
      Pages['LEGAL_IDENTIFIERS']
    ),
  },
};

export const hardcodedContactFields: ICustomField[] = [
  {
    label: 'First Name',
    type: FieldTypeEnum.TextBox,
    name: 'firstName',
    required: true,
  },
  {
    type: FieldTypeEnum.TextBox,
    label: 'Last Name',
    name: 'lastName',
  },
  {
    label: 'Email Address',
    type: FieldTypeEnum.TextBox,
    name: 'emailAddress',
    required: true,
  },
  {
    label: 'Position',
    type: FieldTypeEnum.TextBox,
    name: 'position',
  },
  {
    label: 'Phone Number',
    type: FieldTypeEnum.TextBox,
    name: 'phoneNumber',
  },
  {
    label: 'LinkedIn Profile',
    type: FieldTypeEnum.URL,
    name: 'linkedinProfile',
    required: true,
  },
  {
    label: 'Location',
    type: FieldTypeEnum.Location,
    name: 'location',
  },
  {
    label: 'Tags',
    type: FieldTypeEnum.Tags,
    name: 'tags',
  },
  {
    label: 'Client Contact Status',
    type: FieldTypeEnum.Dropdown,
    name: 'markStatus',
  },
  {
    label: 'Client Contact Owner',
    type: FieldTypeEnum.Dropdown,
    name: 'ownerId',
    required: true,
  },
  {
    label: 'Supervisor',
    type: FieldTypeEnum.Dropdown,
    name: 'supervisorEmailAddress',
  },
].map((field) => ({ ...field, isDefault: true }));

export const fieldFormDefaultValues = {
  name: '',
  label: '',
  description: '',
  required: false,
  visible: true,
  type: FieldTypeEnum.TextBox,
  options: [],
  isDefault: false,
};

export const toTitleCaseWithSpaces = (str: string) =>
  str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const mapFieldFormToDTO = (
  field: FieldFormData,
  entityType: CustomFieldEnum
): ICustomField => ({
  ...field,
  entity: entityType,
  options: field.options?.map((option) => option.value) || [],
});

export interface FieldFormData extends Omit<ICustomField, 'options'> {
  options: { value: string }[];
}

export const formatCustomFieldValue = (field: ICustomField, value: any): string => {
  if (!value) return '';
  switch (field.type) {
    case FieldTypeEnum.Location:
      return (
        [value.city, value.state, value.country].filter(Boolean).join(', ') || value.continent || ''
      );
    case FieldTypeEnum.Date:
      return getFormatDate(value.toString());
    case FieldTypeEnum.DateAndTime:
      return activityFormattedDate(value.toString());
    case FieldTypeEnum.Users:
      return value?.[0] || '';
    case FieldTypeEnum.Dropdown:
      return field.multiple ? value?.join(', ') : value;
    case FieldTypeEnum.Checkbox:
      return field?.options?.length === 0 ? (value ? 'Yes' : 'No') : value;
    default:
      return value?.toString?.() || '';
  }
};

interface SubmitCustomFieldParams {
  entityType: CustomFieldEnum;
  formData: FieldFormData;
  createService: (data: ICustomField) => void;
  updateService: (data: ICustomField) => void;
  onSave: () => void;
  notify: (msg: ReturnType<typeof success | typeof warning | typeof failed>) => void;
}

export const submitCustomField = async ({
  entityType,
  formData,
  createService,
  updateService,
  onSave,
  notify,
}: SubmitCustomFieldParams) => {
  try {
    const mappedData = mapFieldFormToDTO(formData, entityType);

    if (formData.id) {
      await updateService(mappedData);
    } else {
      await createService({ ...mappedData, isDefault: false });
    }

    notify(success('Custom Field saved successfully!'));
    onSave();
  } catch (error: any) {
    if (error?.response?.status === 400) {
      notify(warning('Oops, already exists!!'));
      console.error('Error status:', error);
    } else {
      notify(failed('Something went wrong !!'));
      console.error('Error occurred:', error);
    }
  }
};

export const customFieldFormSchema = object().shape({
  label: string().required('Field Name is required'),
});

export interface FieldOption {
  id: string;
  label?: string;
  checked: boolean;
}

export const mergeCustomFieldSettings = (
  baseSettings: FieldOption[],
  customFields?: ICustomField[] | null
): FieldOption[] => {
  if (!customFields?.length) return baseSettings;
  const existingIds = new Set(baseSettings.map((s) => s.id));
  const customFieldSettings: FieldOption[] = customFields
    .filter((field): field is ICustomField & { name: string } => typeof field.name === 'string')
    .filter((field) => !existingIds.has(`customFields.${field.name}`))
    .map((field) => ({
      id: `customFields.${field.name}`,
      label: field.label ?? field.name,
      checked: false,
    }));
  return [...baseSettings, ...customFieldSettings];
};
